
/* Smart complementary cart recommendations for Prestige.
 *
 * Shopify Search & Discovery complementary products only.
 * No manual Cross Sell fallback.
 * No caching.
 * Progressive loading as requests complete.
 * Maximum 10 products in round-robin order.
 * Excludes products already in cart and duplicates.
 * Supports Prestige Quick Buy and carousel dots.
 */

(() => {
  if (customElements.get('cart-smart-recommendations')) return;

  class CartSmartRecommendations extends HTMLElement {
    constructor() {
      super();

      this.requestNumber = 0;
      this.lastCartSignature = null;
      this.renderedSignature = null;
      this.refreshTimer = null;
      this.itemObserver = null;
      this.drawerObserver = null;
      this.scrollFrame = null;
      this.loaded = false;
      this.loading = false;

      this.onCartChange = () => this.scheduleRefresh();
      this.onCarouselScroll = () => this.scheduleDotUpdate();
      this.onDotClick = event => this.handleDotClick(event);
    }

    connectedCallback() {
      this.carousel = this.querySelector(
        '[data-smart-carousel]'
      );

      this.header = this.querySelector(
        '[data-smart-header]'
      );

      this.heading = this.querySelector(
        '[data-smart-heading]'
      );

      this.dots = this.querySelector(
        '[data-smart-dots]'
      );

      this.maxCards = Math.max(
        1,
        Math.min(10, Number(this.dataset.maxCards) || 10)
      );

      this.lastCartSignature = null;
      this.renderedSignature = null;
      this.loaded = false;
      this.loading = false;

      if (this.carousel) {
        this.carousel.replaceChildren();

        this.carousel.addEventListener(
          'scroll',
          this.onCarouselScroll,
          { passive: true }
        );
      }

      this.hidden = true;

      if (this.dots) {
        this.dots.addEventListener(
          'click',
          this.onDotClick
        );
      }

      document.addEventListener(
        'cart:change',
        this.onCartChange
      );

      document.addEventListener(
        'cart:refresh',
        this.onCartChange
      );

      this.observeCartItems();
      this.observeDrawer();

      // Load immediately when the drawer component connects.
      this.refresh();
    }

    disconnectedCallback() {
      ++this.requestNumber;

      document.removeEventListener(
        'cart:change',
        this.onCartChange
      );

      document.removeEventListener(
        'cart:refresh',
        this.onCartChange
      );

      this.carousel?.removeEventListener(
        'scroll',
        this.onCarouselScroll
      );

      this.dots?.removeEventListener(
        'click',
        this.onDotClick
      );

      this.itemObserver?.disconnect();
      this.drawerObserver?.disconnect();

      clearTimeout(this.refreshTimer);

      if (this.scrollFrame !== null) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
    }

    observeCartItems() {
      this.itemObserver?.disconnect();

      const items = this.closest('cart-drawer')
        ?.querySelector('.cart-drawer__items');

      if (!items) return;

      this.itemObserver = new MutationObserver(() => {
        this.scheduleRefresh();
      });

      this.itemObserver.observe(items, {
        childList: true,
        subtree: true
      });
    }

    observeDrawer() {
      this.drawerObserver?.disconnect();

      const drawer = this.closest('cart-drawer');

      if (!drawer) return;

      this.drawerObserver = new MutationObserver(() => {
        if (drawer.hasAttribute('open')) {
          this.scheduleRefresh();
        }
      });

      this.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['open']
      });
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);

      this.refreshTimer = setTimeout(() => {
        this.refresh();
      }, 120);
    }

    async refresh() {
      // Avoid restarting requests unnecessarily.
      if (this.loading) return;

      this.loading = true;

      const requestNumber = ++this.requestNumber;

      const root = window.Shopify?.routes?.root || '/';
      const base = root.endsWith('/') ? root : `${root}/`;

      let cart;

      try {
        const response = await fetch(`${base}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error('Unable to read cart');
        }

        cart = await response.json();
      } catch (error) {
        if (
          this.isConnected &&
          requestNumber === this.requestNumber
        ) {
          this.loading = false;
          this.loaded = false;
        }

        return;
      }

      if (
        !this.isConnected ||
        requestNumber !== this.requestNumber
      ) return;

      const cartIds = [
        ...new Set(
          cart.items.map(item => String(item.product_id))
        )
      ];

      const signature = cartIds.join(',');

      if (
        this.loaded &&
        signature === this.lastCartSignature
      ) {
        this.loading = false;
        return;
      }

      this.lastCartSignature = signature;
      this.loaded = false;

      if (!cartIds.length) {
        this.replaceCards([]);
        this.loading = false;
        this.loaded = true;
        return;
      }

      const recommendationUrl =
        this.dataset.recommendationsUrl ||
        `${base}recommendations/products`;

      const recommendationLimit = Math.max(
        1,
        Math.min(
          10,
          Number(this.dataset.perProductLimit) || 10
        )
      );

      const groups = new Array(cartIds.length).fill(null);
      let completed = 0;
      let successful = 0;

      const updateResults = () => {
        if (
          !this.isConnected ||
          requestNumber !== this.requestNumber
        ) return;

        const usedIds = new Set(cartIds);
        const chosen = [];

        const longest = Math.max(
          0,
          ...groups.map(group => group?.length || 0)
        );

        // Round robin across each cart product.
        for (
          let index = 0;
          index < longest && chosen.length < this.maxCards;
          index++
        ) {
          for (const group of groups) {
            const node = group?.[index];

            if (!node) continue;

            const id = node.dataset.productId;

            if (!id || usedIds.has(id)) continue;

            usedIds.add(id);
            chosen.push(node);

            if (chosen.length >= this.maxCards) break;
          }
        }

        const newSignature = chosen
          .map(node => node.dataset.productId)
          .join(',');

        if (this.renderedSignature !== newSignature) {
          this.renderedSignature = newSignature;
          this.replaceCards(chosen);
        }

        if (completed === cartIds.length) {
          this.loading = false;
          this.loaded = successful > 0;

          if (this.refreshPending) {
            this.refreshPending = false;
            this.scheduleRefresh();
          }
        }
      };

      // Each request renders independently when complete.
      cartIds.forEach((id, index) => {
        const url = new URL(
          recommendationUrl,
          window.location.origin
        );

        url.searchParams.set(
          'section_id',
          'cart-recommendation-items'
        );

        url.searchParams.set(
          'product_id',
          id
        );

        url.searchParams.set(
          'limit',
          String(recommendationLimit)
        );

        url.searchParams.set(
          'intent',
          'complementary'
        );

        fetch(url, {
          credentials: 'same-origin'
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `Recommendations unavailable for ${id}`
              );
            }

            return response.text();
          })
          .then(html => {
            const doc = new DOMParser().parseFromString(
              html,
              'text/html'
            );

            groups[index] = Array.from(
              doc.querySelectorAll(
                '[data-smart-recommendation-card]'
              )
            );

            successful++;
          })
          .catch(error => {
            groups[index] = [];

            console.warn(
              'Cart complementary recommendations:',
              error
            );
          })
          .finally(() => {
            completed++;
            updateResults();
          });
      });
    }

    getCards() {
      if (!this.carousel) return [];

      return Array.from(
        this.carousel.querySelectorAll(
          '.horizontal-product-card'
        )
      );
    }

    handleDotClick(event) {
      const button = event.target.closest('button');

      if (!button || !this.dots?.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const buttons = Array.from(
        this.dots.querySelectorAll('button')
      );

      const index = buttons.indexOf(button);

      if (index < 0) return;

      this.scrollToCard(index);
    }

    scrollToCard(index) {
      if (!this.carousel) return;

      const cards = this.getCards();
      const card = cards[index];

      if (!card) return;

      const carouselRect =
        this.carousel.getBoundingClientRect();

      const cardRect = card.getBoundingClientRect();

      const target =
        this.carousel.scrollLeft +
        cardRect.left -
        carouselRect.left;

      this.carousel.scrollTo({
        left: target,
        behavior: 'smooth'
      });

      this.setActiveDot(index);
    }

    setActiveDot(index) {
      if (!this.dots) return;

      const buttons = Array.from(
        this.dots.querySelectorAll('button')
      );

      buttons.forEach((button, buttonIndex) => {
        button.setAttribute(
          'aria-current',
          buttonIndex === index ? 'true' : 'false'
        );
      });
    }

    scheduleDotUpdate() {
      if (this.scrollFrame !== null) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateActiveDot();
      });
    }

    updateActiveDot() {
      if (!this.carousel || !this.dots) return;

      const cards = this.getCards();

      if (!cards.length) return;

      const carouselRect =
        this.carousel.getBoundingClientRect();

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();

        const distance = Math.abs(
          rect.left - carouselRect.left
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.setActiveDot(closestIndex);
    }

    replaceCards(nodes) {
      if (!this.carousel) return;

      const fragment = document.createDocumentFragment();

      // Preserve the current carousel position where possible.
      const previousScroll = this.carousel.scrollLeft;

      for (const wrapper of nodes) {
        const card = wrapper.firstElementChild;

        if (card) {
          fragment.appendChild(card.cloneNode(true));
        }
      }

      this.carousel.replaceChildren(fragment);

      this.hidden = nodes.length === 0;

      if (this.header) {
        this.header.hidden = nodes.length === 0;
      }

      if (this.heading) {
        this.heading.hidden =
          !this.heading.textContent.trim();
      }

      if (this.dots) {
        const dotsFragment =
          document.createDocumentFragment();

        nodes.forEach((_, index) => {
          const button = document.createElement('button');

          button.type = 'button';
          button.className = 'relative';

          button.setAttribute(
            'aria-current',
            index === 0 ? 'true' : 'false'
          );

          button.setAttribute(
            'aria-label',
            `Go to item ${index + 1}`
          );

          const span = document.createElement('span');

          span.className = 'sr-only';
          span.textContent = `Go to item ${index + 1}`;

          button.appendChild(span);
          dotsFragment.appendChild(button);
        });

        this.dots.replaceChildren(dotsFragment);
        this.dots.hidden = nodes.length <= 1;
      }

      // Restore the scroll position after updating products.
      this.carousel.scrollLeft = previousScroll;

      requestAnimationFrame(() => {
        if (this.isConnected) {
          this.updateActiveDot();
        }
      });
    }
  }

  customElements.define(
    'cart-smart-recommendations',
    CartSmartRecommendations
  );
})();
